import { useCallback } from 'react';
import { useAppContext } from '../context/AppContext';
import { enqueueOp, deleteHuntLocal, deletePendingBlob } from '../lib/db';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db as firestore, storage } from '../firebase/firebaseConfig';
import { implicitTitle } from '../lib/huntDomain';

export function useHunts() {
  const { state, user, isOnline, upsertHuntState, removePhotoState, dispatch } = useAppContext();

  const writeHunt = useCallback(async (hunt) => {
    await upsertHuntState(hunt);

    if (isOnline && user) {
      try {
        await setDoc(doc(firestore, 'users', user.uid, 'hunts', hunt.id), hunt);
      } catch (err) {
        console.error('Firebase hunt write failed, queuing for later:', err);
        await enqueueOp('setHunt', { userId: user.uid, hunt });
      }
    } else if (user) {
      await enqueueOp('setHunt', { userId: user.uid, hunt });
    }

    return hunt;
  }, [user, isOnline, upsertHuntState]);

  const createHunt = useCallback(async ({ type, title }) => {
    const now = new Date().toISOString();
    const hunt = {
      id: crypto.randomUUID(),
      type,
      title: type === 'custom' ? title.trim() : implicitTitle(type),
      huntList: [],
      createdAt: now,
      updatedAt: now,
    };
    await writeHunt(hunt);
    dispatch({ type: 'SET_ACTIVE_HUNT', huntId: hunt.id });
    return hunt;
  }, [writeHunt, dispatch]);

  const updateHuntList = useCallback(async (hunt, huntList) => {
    await writeHunt({ ...hunt, huntList, updatedAt: new Date().toISOString() });
  }, [writeHunt]);

  const deleteHunt = useCallback(async (hunt) => {
    const huntPhotos = state.photos.filter(p => p.huntId === hunt.id);

    for (const photo of huntPhotos) {
      await removePhotoState(photo.id);
      await deletePendingBlob(photo.id);

      if (isOnline && user) {
        try {
          await deleteDoc(doc(firestore, 'users', user.uid, 'photos', photo.id));
          const storageRef = ref(storage, `users/${user.uid}/thumbnails/${photo.id}.jpg`);
          await deleteObject(storageRef).catch(() => {}); // ignore not-found
        } catch (err) {
          console.error('Firebase photo delete failed, queuing for later:', err);
          await enqueueOp('delete', { userId: user.uid, photoId: photo.id });
        }
      } else if (user) {
        await enqueueOp('delete', { userId: user.uid, photoId: photo.id });
      }
    }

    await deleteHuntLocal(hunt.id);
    dispatch({ type: 'DELETE_HUNT', id: hunt.id });

    if (isOnline && user) {
      try {
        await deleteDoc(doc(firestore, 'users', user.uid, 'hunts', hunt.id));
      } catch (err) {
        console.error('Firebase hunt delete failed, queuing for later:', err);
        await enqueueOp('deleteHunt', { userId: user.uid, huntId: hunt.id });
      }
    } else if (user) {
      await enqueueOp('deleteHunt', { userId: user.uid, huntId: hunt.id });
    }
  }, [user, isOnline, state.photos, removePhotoState, dispatch]);

  return { createHunt, updateHuntList, deleteHunt };
}
